import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import tenantService from '../../services/tenantService';

// Material UI components
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  Avatar,
  IconButton,
  Alert,
  Snackbar,
  Skeleton,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';

// Material UI icons
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon,
  Schedule as ClockIcon,
  Visibility as EyeIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Business as BusinessIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PauseCircleOutline as PauseCircleOutlineIcon
} from '@mui/icons-material';

// Status badge component for tenant status
const StatusBadge = ({ status }) => {
  const statusConfig = {
    active: {
      color: 'success',
      icon: <CheckCircleIcon fontSize="small" />
    },
    pending: {
      color: 'warning',
      icon: <ClockIcon fontSize="small" />
    },
    suspended: {
      color: 'error',
      icon: <CancelIcon fontSize="small" />
    },
    default: {
      color: 'default',
      icon: <WarningIcon fontSize="small" />
    }
  };

  const config = statusConfig[status] || statusConfig.default;

  return (
    <Chip
      icon={config.icon}
      label={status || 'unknown'}
      color={config.color}
      size="small"
      variant="outlined"
    />
  );
};

const SuperAdminTenantsPage = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [filteredTenants, setFilteredTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  
  // Add new state variables for suspension dialog
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [tenantToSuspend, setTenantToSuspend] = useState(null);
  
  // Add new state variables for delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState(null);
  
  // Filtering and sorting state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  useEffect(() => {
    loadTenants();
  }, []);
  
  useEffect(() => {
    // Apply filters when dependencies change
    filterTenants();
  }, [tenants, searchTerm, statusFilter, sortBy, sortDirection]);
  
  const loadTenants = async () => {
    try {
      setLoading(true);
      const data = await tenantService.getAllTenants();
      
      // Enhanced tenant data processing
      const enhancedData = data?.map(tenant => {
        // Format admin name properly if available or parse from adminEmail
        let adminName = 'N/A';
        
        if (tenant.adminFirstName && tenant.adminLastName) {
          // If we have first and last name directly
          adminName = `${tenant.adminFirstName} ${tenant.adminLastName}`;
        } else if (tenant.adminName) {
          // If we have a combined name
          adminName = tenant.adminName;
        } else if (tenant.adminId && tenant.admins && tenant.admins.length > 0) {
          // If we have admins array with the primary admin
          const primaryAdmin = tenant.admins.find(admin => admin._id === tenant.adminId);
          if (primaryAdmin) {
            adminName = `${primaryAdmin.firstName || ''} ${primaryAdmin.lastName || ''}`.trim();
          }
        }
        
        return {
          ...tenant,
          formattedAdminName: adminName
        };
      });
      
      setTenants(enhancedData || []);
      setError(null);
    } catch (err) {
      setError('Failed to load tenants. Please try again.');
      console.error('Error loading tenants:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Add null checks to filterTenants function
  const filterTenants = () => {
    let filtered = [...tenants];
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(tenant => 
        tenant.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tenant.subdomain?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tenant.adminEmail?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(tenant => tenant.status === statusFilter);
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      
      if (sortBy === 'name') {
        comparison = a.name.localeCompare(b.name);
      } else if (sortBy === 'createdAt') {
        comparison = new Date(a.createdAt) - new Date(b.createdAt);
      } else if (sortBy === 'status') {
        comparison = a.status.localeCompare(b.status);
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    setFilteredTenants(filtered);
  };

  const handleApproveTenant = async (tenantId) => {
    try {
      await tenantService.approveTenant(tenantId);
      setSuccessMessage('Tenant approved successfully');
      setOpenSnackbar(true);
      loadTenants(); // Reload the list
    } catch (err) {
      setError('Failed to approve tenant. Please try again.');
      setOpenSnackbar(true);
      console.error('Error approving tenant:', err);
    }
  };

  // Update handleSuspendTenant to open the dialog instead of using prompt()
  const handleSuspendTenant = (tenantId) => {
    // Set the tenant ID and open the dialog
    setTenantToSuspend(tenantId);
    setSuspendReason('');
    setSuspendDialogOpen(true);
  };
  
  // New function to handle the actual suspension after dialog confirmation
  const confirmSuspendTenant = async () => {
    try {
      if (suspendReason.trim() && tenantToSuspend) {
        await tenantService.suspendTenant(tenantToSuspend, suspendReason);
        setSuccessMessage('Tenant suspended successfully');
        setOpenSnackbar(true);
        setSuspendDialogOpen(false);
        loadTenants(); // Reload the list
      }
    } catch (err) {
      setError('Failed to suspend tenant. Please try again.');
      setOpenSnackbar(true);
      console.error('Error suspending tenant:', err);
    }
  };

  const handleReactivateTenant = async (tenantId) => {
    try {
      await tenantService.reactivateTenant(tenantId);
      setSuccessMessage('Tenant reactivated successfully');
      setOpenSnackbar(true);
      loadTenants(); // Reload the list
    } catch (err) {
      setError('Failed to reactivate tenant. Please try again.');
      setOpenSnackbar(true);
      console.error('Error reactivating tenant:', err);
    }
  };
  
  // Update handleDeleteTenant to open the dialog instead of using window.confirm
  const handleOpenDeleteDialog = (tenantId) => {
    // Set the tenant ID and open the dialog
    setTenantToDelete(tenantId);
    setDeleteDialogOpen(true);
  };
  
  // New function to handle the actual deletion after dialog confirmation
  const confirmDeleteTenant = async () => {
    try {
      await tenantService.deleteTenant(tenantToDelete);
      setSuccessMessage('Organization deleted successfully');
      setOpenSnackbar(true);
      setDeleteDialogOpen(false);
      loadTenants(); // Reload the list
    } catch (err) {
      setError('Failed to delete organization. Please try again.');
      setOpenSnackbar(true);
      console.error('Error deleting tenant:', err);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };
  
  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Stats calculation
  const stats = {
    total: tenants.length,
    active: tenants.filter(t => t.status === 'active').length,
    pending: tenants.filter(t => t.status === 'pending').length,
    suspended: tenants.filter(t => t.status === 'suspended').length
  };
  
  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box sx={{ p: 3, mt: { xs: 8, md: 2 } }}>
      <Container maxWidth="xl">
        <Box my={4}>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            Organization Management
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            View, manage, and onboard organizations in the system
          </Typography>
        </Box>
        
        {/* Quick Stats */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 2, 
                display: 'flex', 
                alignItems: 'center', 
                borderLeft: '4px solid #3f51b5',
                height: '100%' 
              }}
            >
              <Avatar sx={{ bgcolor: 'rgba(63, 81, 181, 0.1)', color: '#3f51b5' }}>
                <BusinessIcon />
              </Avatar>
              <Box ml={2}>
                <Typography variant="body2" color="text.secondary">Total Organizations</Typography>
                <Typography variant="h5">{stats.total}</Typography>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 2, 
                display: 'flex', 
                alignItems: 'center', 
                borderLeft: '4px solid #4caf50',
                height: '100%'
              }}
            >
              <Avatar sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)', color: '#4caf50' }}>
                <CheckCircleIcon />
              </Avatar>
              <Box ml={2}>
                <Typography variant="body2" color="text.secondary">Active</Typography>
                <Typography variant="h5">{stats.active}</Typography>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 2, 
                display: 'flex', 
                alignItems: 'center', 
                borderLeft: '4px solid #ff9800',
                height: '100%'
              }}
            >
              <Avatar sx={{ bgcolor: 'rgba(255, 152, 0, 0.1)', color: '#ff9800' }}>
                <ClockIcon />
              </Avatar>
              <Box ml={2}>
                <Typography variant="body2" color="text.secondary">Pending</Typography>
                <Typography variant="h5">{stats.pending}</Typography>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 2, 
                display: 'flex', 
                alignItems: 'center', 
                borderLeft: '4px solid #f44336',
                height: '100%'
              }}
            >
              <Avatar sx={{ bgcolor: 'rgba(244, 67, 54, 0.1)', color: '#f44336' }}>
                <PauseCircleOutlineIcon /> {/* Changed from CancelIcon to PauseCircleOutlineIcon */}
              </Avatar>
              <Box ml={2}>
                <Typography variant="body2" color="text.secondary">Suspended</Typography>
                <Typography variant="h5">{stats.suspended}</Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Alert messages */}
        <Snackbar open={openSnackbar} autoHideDuration={3000} onClose={handleCloseSnackbar}>
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={error ? "error" : "success"} 
            variant="filled"
          >
            {error || successMessage}
          </Alert>
        </Snackbar>
        
        {/* Search and Filters */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            mb: 4, 
            borderRadius: 2,
            borderLeft: '4px solid #3f51b5'
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search organizations..."
                variant="outlined"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel id="status-filter-label">Status</InputLabel>
                <Select
                  labelId="status-filter-label"
                  id="status-filter"
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel id="sort-by-label">Sort By</InputLabel>
                <Select
                  labelId="sort-by-label"
                  id="sort-by"
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="name">Name</MenuItem>
                  <MenuItem value="createdAt">Date Created</MenuItem>
                  <MenuItem value="status">Status</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel id="sort-direction-label">Order</InputLabel>
                <Select
                  labelId="sort-direction-label"
                  id="sort-direction"
                  value={sortDirection}
                  label="Order"
                  onChange={(e) => setSortDirection(e.target.value)}
                >
                  <MenuItem value="asc">Ascending</MenuItem>
                  <MenuItem value="desc">Descending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                component={Link}
                to="/superadmin/tenants/add"
                color="primary"
                sx={{
                  background: 'linear-gradient(45deg, #3f51b5 30%, #5c6bc0 90%)',
                  boxShadow: '0 4px 6px rgba(63, 81, 181, 0.25)',
                }}
              >
                Add Organization
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Tenants Table */}
        <Paper 
          elevation={2} 
          sx={{ 
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          <Box 
            sx={{ 
              px: 3, 
              py: 2, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              borderBottom: 1,
              borderColor: 'divider'
            }}
          >
            <Typography variant="h6" component="h2">
              Organizations
            </Typography>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={loadTenants}
            >
              Refresh
            </Button>
          </Box>
          
          {loading ? (
            <Box p={3}>
              {[1, 2, 3, 4, 5].map((item) => (
                <Box key={item} mb={2}>
                  <Skeleton variant="rectangular" height={40} />
                </Box>
              ))}
            </Box>
          ) : filteredTenants.length === 0 ? (
            <Box p={4} textAlign="center">
              <Typography color="text.secondary" gutterBottom>No organizations found.</Typography>
              <Button 
                variant="outlined" 
                startIcon={<AddIcon />}
                component={Link}
                to="/superadmin/tenants/add"
              >
                Add Your First Organization
              </Button>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: 'rgba(0, 0, 0, 0.03)' }}>
                    <TableRow>
                      <TableCell>Organization</TableCell>
                      <TableCell>Subdomain</TableCell>
                      <TableCell>Admin</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTenants
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((tenant, index) => (
                        <TableRow 
                          key={tenant?._id || tenant?.id || index} 
                          hover
                          sx={{ '&:hover': { bgcolor: 'rgba(63,81,181,0.04)' } }}
                        >
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <Avatar 
                                sx={{ 
                                  bgcolor: 'rgba(63,81,181,0.1)', 
                                  color: '#3f51b5',
                                  mr: 2
                                }}
                              >
                                {tenant?.name ? tenant.name.substring(0, 2).toUpperCase() : 'TN'}
                              </Avatar>
                              <Box>
                                <Typography variant="body1" fontWeight="medium">
                                  {tenant?.name || 'Unnamed Organization'}
                                </Typography>
                                {tenant?.plan && (
                                  <Typography variant="caption" color="text.secondary">
                                    {tenant.plan.name || 'Standard Plan'}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography 
                              variant="body2"
                              component="a"
                              href={`https://${tenant?.subdomain}.${window.location.hostname}`}
                              target="_blank"
                              sx={{ 
                                color: 'primary.main',
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                            >
                              {tenant?.subdomain}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {/* Use the formatted admin name or fallback options */}
                              {tenant?.formattedAdminName || 
                               (tenant?.firstName && tenant?.lastName && `${tenant.firstName} ${tenant.lastName}`) ||
                               tenant?.adminEmail?.split('@')[0] || 
                               'No Admin'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {tenant?.adminEmail || 'No Email'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={tenant?.status} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(tenant?.createdAt)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Box display="flex" justifyContent="flex-end">
                              {/* View button */}
                              <Tooltip title="View details">
                                <IconButton 
                                  size="small" 
                                  color="primary"
                                  component={Link}
                                  to={`/superadmin/tenants/${tenant?._id || tenant?.id}`}
                                  aria-label="View details"
                                >
                                  <EyeIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              
                              {/* Edit button */}
                              <Tooltip title="Edit tenant">
                                <IconButton 
                                  size="small" 
                                  color="primary"
                                  component={Link}
                                  to={`/superadmin/tenants/${tenant?._id || tenant?.id}/edit`}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              
                              {/* Conditional action buttons */}
                              {tenant?.status === 'pending' && (
                                <Tooltip title="Approve tenant">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleApproveTenant(tenant?._id || tenant?.id)}
                                  >
                                    <CheckCircleIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {tenant?.status !== 'suspended' && (
                                <Tooltip title="Suspend tenant">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleSuspendTenant(tenant?._id || tenant?.id)}
                                  >
                                    <PauseCircleOutlineIcon fontSize="small" /> {/* Changed from CancelIcon to PauseCircleOutlineIcon */}
                                  </IconButton>
                                </Tooltip>
                              )}
                              {tenant?.status === 'suspended' && (
                                <Tooltip title="Reactivate tenant">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleReactivateTenant(tenant?._id || tenant?.id)}
                                  >
                                    <CheckCircleIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              
                              {/* Delete button */}
                              <Tooltip title="Delete tenant">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleOpenDeleteDialog(tenant?._id || tenant?.id)}
                                  aria-label="Delete organization"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredTenants.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </Paper>

        {/* Add Suspension Dialog */}
        <Dialog
          open={suspendDialogOpen}
          onClose={() => setSuspendDialogOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PauseCircleOutlineIcon color="error" />
              Suspend Organization
            </Box>
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Please provide a reason for suspending this organization. This reason may be displayed to the tenant administrators.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="Suspension Reason"
              fullWidth
              multiline
              rows={4}
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSuspendDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={confirmSuspendTenant} 
              color="error"
              disabled={!suspendReason.trim()}
              startIcon={<PauseCircleOutlineIcon />}
            >
              Suspend
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DeleteIcon color="error" />
              Delete Organization
            </Box>
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Are you sure you want to delete this organization? This action cannot be undone.
            </DialogContentText>
            <Alert severity="warning">
              All users, data, and settings will be permanently deleted.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={confirmDeleteTenant} 
              color="error"
              variant="contained"
              startIcon={<DeleteIcon />}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default SuperAdminTenantsPage;
